import React, { useContext, useEffect, useState } from 'react';
import { ContentChart } from '@meteor/frontend-core';
import { AppContext } from '../../../../contexts/AppContext';
import { GLOBAL_EVENT } from '../../../../consts/common/global-event';
import APIList from '../../../../http/ApiList';
import { cloneDeep } from 'lodash';
import ErrorBoundary from '../../../common/error-boundary';

const CustomizeScatterChangeableChart: React.FC<any> = (props) => {
  const { authInfo, echartTheme, globalEvent, setGlobalEvent, color } = useContext(AppContext);
  const { property } = props;
  const [chartLoading, setChartLoading] = useState<any>(false);
  const [chartData, setChartData] = useState<any>(null);
  const defaultChartOptions: any = {
    tooltip: {
      formatter: (params) => {
        return (
          `${params.marker}${params.seriesName}` +
          '<br/>' +
          `ROIC: ${(params.data[0] * 100).toFixed(2)}%` +
          '<br/>' +
          `前年比伸び率: ${(params.data[1] * 100).toFixed(2)}%` +
          '<br/>' +
          `売上: ${params.data[2]}`
        );
      },
    },
    legend: {
      top: 0,
      type: 'scroll',
      width: '70%',
    },
    grid: {
      bottom: 90,
      top: 30,
      right: 120,
      left: 40,
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: (value) => (value * 100).toFixed(0) + '%',
      },
      min: -0.07,
      max: 0.15,
    },
    xAxis: {
      type: 'value',
      axisLabel: {
        formatter: (value) => (value * 100).toFixed(0) + '%',
      },
      min: 0,
      max: 0.12,
    },

    animationDurationUpdate: 1000,
    animationEasingUpdate: 'quinticInOut',
    timeline: {
      axisType: 'category',
      orient: 'vertical',
      autoPlay: true,
      inverse: true,
      playInterval: 3000,
      left: null,
      right: 0,
      top: 30,
      bottom: 70,
      width: 45,
      height: null,
      checkpointStyle: {
        borderWidth: 2,
        symbolSize: 10,
        color: color.primaryColor,
      },
      controlStyle: {
        showNextBtn: false,
        showPrevBtn: false,
        itemSize: 16,
        borderColor: color.primaryColor,
        color: color.primaryColor,
        shadowColor: color.primaryColor,
      },
      itemStyle: {
        color: color.tertiaryColor,
      },
      lineStyle: { color: color.tertiaryColor },
      symbolSize: 8,
      progress: {
        lineStyle: { color: color.primaryColor },
        itemStyle: { color: color.primaryColor },
      },
      data: ['FY23', 'FY24', 'FY25'],
    },
  };
  defaultChartOptions.series = [
    {
      type: 'scatter',
      name: 'P＆Cベーシック',
    },
    {
      type: 'scatter',
      name: '機能品ベーシック',
    },
    {
      type: 'scatter',
      name: '医薬',
    },
    {
      type: 'scatter',
      name: '機能品スペシャリティ',
    },
    {
      type: 'scatter',
      name: 'エラストマー',
    },
    {
      type: 'scatter',
      name: 'P＆Cスペシャリティ',
    },
  ];
  const [options, setOptions] = useState<any>(defaultChartOptions);

  const getScatterData = (chartData) => {
    return chartData.map((d, i) => {
      const seriesArray = d.map((s, i) => {
        return {
          data: s,
          symbolSize: (d) => {
            return d[2] * 0.05;
          },
        };
      });
      return { series: seriesArray };
    });
  };

  const getChartData = (event: string) => {
    APIList.getCustomizeLayoutData()
      .post({ email: authInfo?.user?.attributes?.email || '', property, event })
      .then((res: any) => {
        setChartData(res.data);
        setChartLoading(false);
      });
  };

  useEffect(() => {
    if (!property || !property.chartOptions || !property.chartOptions.options) {
      return;
    }
    try {
      let op;
      if (property.chartOptions.options) {
        const reviver = (key, value) => {
          const arrowFunctionRegex = /^\s*\(?\s*([^)]*)\s*\)?\s*=>\s*{[^]*}$/;
          if (typeof value === 'string' && (value.startsWith('function') || arrowFunctionRegex.test(value))) {
            return new Function('return ' + value)();
          }
          return value;
        };
        const tempOp = JSON.parse(property.chartOptions.options, reviver);
        op = {
          ...options,
          ...tempOp,
        };
      } else {
        op = defaultChartOptions;
      }
      setOptions(cloneDeep({ ...op, echartTheme }));
    } catch (error) {}
  }, [property]);

  useEffect(() => {
    if (!globalEvent) {
      return;
    }
    if (
      (property.dataSource &&
        property.dataSource.triggerEvent &&
        property.dataSource.triggerEvent.find((triggerEvent) => {
          return triggerEvent.event === globalEvent.eventType;
        })) ||
      globalEvent.eventType === GLOBAL_EVENT.PAGE_INIT
    ) {
      setChartLoading(true);
      setTimeout(() => {
        getChartData(globalEvent);
      }, 500);
    }
  }, [globalEvent]);

  useEffect(() => {
    if (!chartData || chartData.length === 0) {
      return;
    }
    options.options = getScatterData(chartData);
    setOptions(cloneDeep({ ...options, echartTheme }));
  }, [chartData]);

  return (
    <ErrorBoundary>
      <ContentChart
        title={{
          value: (
            <>
              <span>{property.title}</span> <span style={{ float: 'right' }}>{property.subtitle}</span>
            </>
          ),
          styles: {},
        }}
        contentList={[]}
        chartOptions={options}
        showSeriesLabel={false}
        mouseWheelZoomLock={false}
        loading={chartLoading}
        height={'90%'}
        tooltips={['zoomIn']}
        isBank={!chartData || chartData.length === 0}
        onChartEvent={setGlobalEvent}
      />
    </ErrorBoundary>
  );
};

export default CustomizeScatterChangeableChart;
